import { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { app, db } from '../../firebase/firebaseConfig';
import { useMock } from './mockContext';

/* useAccountInfo()

   Returns
     {
       loading,
       userEmail,
       accountUUID,
       accountName,           // accountsInfo.name
       families,              // accountsInfo.families[]   (live-updates)
       primaryFamilyID,       // accountsInfo.primaryFamilyID  (live-updates)
       owningFamilies,        // subset of families where this account UUID is in owners/
     }

   Subscribes to accountsInfo/{uuid} with onSnapshot so renames, primary
   changes, and family additions/removals propagate without a refresh.
   `owningFamilies` is recomputed via getDoc whenever `families` changes
   (owners/ subcollection isn't worth a per-doc listener — ownership is
   essentially write-once, set during the /setup batch).
*/
export function useAccountInfo() {
  const mock = useMock();
  const [state, setState] = useState({
    loading: true,
    userEmail: null,
    accountUUID: null,
    accountName: '',
    families: [],
    primaryFamilyID: null,
    owningFamilies: [],
  });

  useEffect(() => {
    if (mock?.account) return undefined;
    const auth = getAuth(app);
    let detachAccount = null;
    let cancelled = false;

    const off = auth.onAuthStateChanged(async (user) => {
      if (cancelled) return;
      if (detachAccount) {
        detachAccount();
        detachAccount = null;
      }
      if (!user) {
        setState((s) => ({ ...s, loading: false, userEmail: null, accountUUID: null }));
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        const uuid = tokenResult.claims.accountUUID;
        if (!uuid) {
          setState((s) => ({ ...s, loading: false, userEmail: user.email }));
          return;
        }

        detachAccount = onSnapshot(
          doc(db, 'accountsInfo', uuid),
          async (snap) => {
            if (!snap.exists()) {
              setState({
                loading: false,
                userEmail: user.email,
                accountUUID: uuid,
                accountName: '',
                families: [],
                primaryFamilyID: null,
                owningFamilies: [],
              });
              return;
            }
            const d = snap.data();
            const families = Array.isArray(d.families) ? d.families : [];
            // owners is a sub-collection; we look up each family individually.
            // This list rarely changes after setup, so getDoc each time the
            // families array updates is fine.
            const owning = [];
            for (const fid of families) {
              try {
                const ownerDoc = await getDoc(doc(db, 'familyInfo', fid, 'owners', uuid));
                if (ownerDoc.exists()) owning.push(fid);
              } catch (err) {
                console.warn('owner lookup failed', fid, err.code);
              }
            }
            if (cancelled) return;
            setState({
              loading: false,
              userEmail: user.email,
              accountUUID: uuid,
              accountName: d.name || '',
              families,
              primaryFamilyID: d.primaryFamilyID || null,
              owningFamilies: owning,
            });
          },
          (err) => {
            console.warn('accountsInfo listener error', err.code);
            setState((s) => ({ ...s, loading: false }));
          },
        );
      } catch (err) {
        console.error('useAccountInfo init error', err);
        setState((s) => ({ ...s, loading: false }));
      }
    });

    return () => {
      cancelled = true;
      off();
      if (detachAccount) detachAccount();
    };
  }, [mock]);

  if (mock?.account) return mock.account;
  return state;
}
